import Editor from './Editor';
import IconManager from './IconManager';
import GoogleFonts from './GoogleFonts';
import { __ } from "@wordpress/i18n";
import React, { useState, useEffect } from "react";
import { Container } from "@bsf/force-ui";
import NavMenu from "../NavMenu";
import { routes } from "../../admin/settings/routes";
import { history } from "../../router";
import Sidebar from './Sidebar';
import Content from './Content';
import MapSettings from './MapSettings';
import License from './License';
import Debug from './Debug';


const Settings = () => {

  const items = [
    {
      id: 1,
      icon: (
        <img
          src={`${uavcSettingsData.editor_url}`}
          alt={__("Custom SVG", "ultimate_vc")}
          className="object-contain"
        />
      ),
      selected: (
        <img
          src={`${uavcSettingsData.editor__selected_url}`}
          alt={__("Custom SVG", "ultimate_vc")}
          className="object-contain"
        />
      ),
      main: __("License", "ultimate_vc"),
      title: __("Product License", "ultimate_vc"),
      content: <License />,
    },
    {
      id: 2,
      icon: (
        <img
          src={`${uavcSettingsData.user_url}`}
          alt={__("Custom SVG", "ultimate_vc")}
          className="object-contain"
        />
      ),
      selected: (
        <img
          src={`${uavcSettingsData.user__selected_url}`}
          alt={__("Custom SVG", "ultimate_vc")}
          className="object-contain"
        />
      ),
      main: __("Editor", "ultimate_vc"),
      title: __("General Settings", "ultimate_vc"),
      content: <Editor />,
    },
    {
      id: 3,
      icon: (
        <img
          src={`${uavcSettingsData.icon_manager_url}`}
          alt={__('Icon Manager', 'ultimate_vc')}
          className="object-contain"
        />
      ),
      selected: (
        <img
          src={`${uavcSettingsData.icon_manager_selected_url}`}
          alt={__('Icon Manager', 'ultimate_vc')}
          className="object-contain"
        />
      ),
      main: __("Icons", "ultimate_vc"),
      title: __('Icon Manager', 'ultimate_vc'),
      content: <IconManager />, 
    },
    {
      id: 4,
      icon: (
        <img
          src={`${uavcSettingsData.google_fonts_url}`}
          alt={__('Google Fonts', 'ultimate_vc')}
          className="object-contain"
        />
      ),
      selected: (
        <img
          src={`${uavcSettingsData.google_fonts_selected_url}`}
          alt={__('Icon Manager', 'ultimate_vc')}
          className="object-contain"
        />
      ),
      main: __("Google", "ultimate_vc"),
      title: __('Google Fonts', 'ultimate_vc'),
      content: <GoogleFonts />,
    },
    {
      id: 5,
      icon: (
        <img
          src={`${uavcSettingsData.google_map_icon}`}
          alt={__("Google Maps", "ultimate_vc")}
          className="object-contain"
        />
      ),
      selected: (
        <img
          src={`${uavcSettingsData.google_map_selected}`}
          alt={__("Google Maps", "ultimate_vc")}
          className="object-contain"
        />
      ),
      title: __("Google Maps", "ultimate_vc"),
      content: <MapSettings />,
    },
    {
      id: 6,
      icon: (
        <img
        src={`${uavcSettingsData.advanced_url}`}
          alt={__("Debug", "ultimate_vc")}
          className="object-contain"
        />
      ),
      selected: (
        <img
        src={`${uavcSettingsData.advanced__selected_url}`}
          alt={__("Debug", "ultimate_vc")}
          className="object-contain"
        />
      ),
      main: __("Configuration Options", "ultimate_vc"),
      title: __("Advanced Settings", "ultimate_vc"),
      content: <Debug />,
    },
  ];

  // Default state: restore previously selected tab if available, otherwise load the
  // Product License tab. This keeps the tab persistent on page refresh while
  // still allowing menu navigation to reset to the first tab.
  const [selectedItem, setSelectedItem] = useState(() => {
    // First, check if the current URL specifies a tab. When navigating from
    // different menu locations we append `?tab=1` to force the Product License
    // tab, so this takes precedence over any saved preference.
    const params = new URLSearchParams(window.location.search);
    const tabParam = params.get("tab");
    if (tabParam) {
      const itemId = Number(tabParam);
      const item = items.find((item) => item.id === itemId);
      if (item) {
        return item;
      }
    }

    // Otherwise fall back to the previously selected tab stored in localStorage
    const savedItemId = localStorage.getItem("uavcSelectedItemId");
    const savedItem = items.find((item) => item.id === Number(savedItemId));
    return savedItem || items[0];
  });

  useEffect(() => {
    // Store selectedItemId in localStorage (or other persistent storage) to retain selection
    localStorage.setItem("uavcSelectedItemId", selectedItem.id.toString());

    // Keep the current tab in the query string so page refreshes maintain the
    // selected tab even after navigating from other menus.
    const params = new URLSearchParams(window.location.search);
    params.set("tab", selectedItem.id.toString());
    const query = params.toString();
    const newUrl = `${window.location.pathname}${query ? `?${query}` : ""}#${routes.settings.path}`;
    window.history.replaceState(null, "", newUrl);
  }, [selectedItem]);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const tab = params.get("tab");
    if (tab) {
      const itemId = Number(tab);
      const item = items.find((item) => item.id === itemId);
      if (item) {
        setSelectedItem(item);
      }
    }
  }, []);

  // Update the selected tab whenever the query string changes
  useEffect(() => {
    const unlisten = history.listen(({ location }) => {
      const params = new URLSearchParams(location.search);
      const tab = params.get("tab");
      if (tab) {
        const itemId = Number(tab);
        const item = items.find((i) => i.id === itemId);
        if (item) {
          setSelectedItem(item);
        }
      }
    });

    return () => {
      unlisten();
    };
  }, [items]);


  const handleSelectItem = (item) => {
    setSelectedItem(item);
  };

  const handleSettingsTabClick = () => {
    // When navigating to the settings page from the menu, always show the Product License tab first
    setSelectedItem(items[0]);
  };



  return (
    <>
      <NavMenu onSettingsTabClick={handleSettingsTabClick} />
      <div className="">
        <Container
          align="stretch"
          className="p-1 flex-col lg:flex-row uavc-settings-page"
          containerType="flex"
          direction="row"
          gap="sm"
          justify="start"
          style={{ height: "100%" }}
        >
          <Container.Item
            className="p-2 uavc-sticky-outer-wrapper"
            alignSelf="auto"
            order="none"
            shrink={1}
            style={{ backgroundColor: "#ffffff" }}
          >
            <div className="uavc-sticky-sidebar">
              <Sidebar
                items={items}
                onSelectItem={handleSelectItem}
                selectedItemId={selectedItem.id}
              />
            </div>
          </Container.Item>
          <Container.Item
            className="p-2 flex w-full justify-center items-start uavc-hide-scrollbar"
            alignSelf="auto"
            order="none"
            shrink={1}
            style={{
              height: "calc(100vh - 1px)",
              overflowY: "auto",
            }}
          >
            <div className="uavc-78-width">
              <Content selectedItem={selectedItem} />
            </div>
          </Container.Item>
        </Container>
      </div>
    </>
  )
}
export default Settings;

